import { useState } from 'react';
import { GiftIcon, PencilIcon, PlusIcon, TrashIcon } from 'lucide-react';
import { z } from 'zod';
import {
  adminRewardCreateSchema,
  type AdminRewardItem,
  type AdminRewardUpdate,
} from '@qaytim/shared';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Field } from '@/components/field';
import { FormDialog } from '@/components/form-dialog';
import { ImageUploadField } from '@/components/image-upload-field';
import { PageHeader } from '@/components/page-header';
import { numberField, optionalNumberField, trimmed, useAdminForm } from '@/lib/admin-form';
import { EmptyState, ErrorNotice, TableSkeleton } from '@/components/states';
import {
  useAdminRewards,
  useCreateReward,
  useDeleteReward,
  useUpdateReward,
} from '@/lib/admin-queries';
import { formatNumber } from '@/lib/utils';
import { strings } from '@/i18n/strings';

const REWARD_COLUMNS = 6;

/**
 * Yaratish formasining sxemasi.
 *
 * Maydon QOIDALARI shartnomadan olinadi (`adminRewardCreateSchema.shape`), forma esa faqat
 * o'z tashvishlarini qo'shadi: matn kiritishdan kelgan raqamlarni o'girish, bo'sh pozitsiyani
 * "berilmagan" deb o'qish va `<select>` ning satr qiymatini mantiqiy qiymatga aylantirish.
 * Shu tarzda "ball narxi musbat butun son" qoidasi bitta joyda qoladi — server uni
 * o'zgartirsa, forma ham o'zgaradi.
 */
const rewardFormSchema = z.object({
  name: trimmed(adminRewardCreateSchema.shape.name),
  pointsCost: numberField(adminRewardCreateSchema.shape.pointsCost),
  position: optionalNumberField,
  active: z.enum(['active', 'inactive']),
});

const EMPTY_REWARD_FORM = {
  name: '',
  pointsCost: '',
  position: '',
  active: 'active',
} as const;

/** Mavjud bandning qiymatlari — tahrirlash formasi ular bilan boshlanadi. */
function rowValues(reward: AdminRewardItem) {
  return {
    name: reward.name,
    pointsCost: String(reward.pointsCost),
    position: String(reward.position),
    active: reward.active ? ('active' as const) : ('inactive' as const),
  };
}

/**
 * Ball-katalog boshqaruvi (§ P8).
 *
 * Bu FAQAT katalog — "qaysi band nechchi ballga turadi". Almashtirish (buyurtma, qoldiq)
 * bo'yicha ish keyingi bosqichda; bu yerda hech qanday amal foydalanuvchi balansiga tegmaydi.
 */
export function RewardsPage() {
  const rewards = useAdminRewards();
  const createReward = useCreateReward();
  const updateReward = useUpdateReward();
  const deleteReward = useDeleteReward();

  const [createOpen, setCreateOpen] = useState(false);
  const [imageKey, setImageKey] = useState<string | null>(null);
  // Muvaffaqiyatli yaratishdan keyin rasm maydonini qayta tug'dirib, uning ichki holatini
  // (oldindan ko'rish, yuklash holati) ham tozalaydi.
  const [formResetCount, setFormResetCount] = useState(0);

  const form = useAdminForm({ schema: rewardFormSchema, defaultValues: EMPTY_REWARD_FORM });

  const rows = rewards.data?.items ?? [];

  const resetForm = () => {
    form.reset(EMPTY_REWARD_FORM);
    setImageKey(null);
    setFormResetCount((count) => count + 1);
  };

  const submitCreate = form.handleSubmit((values) => {
    createReward.mutate(
      {
        name: values.name,
        pointsCost: values.pointsCost,
        ...(values.position === undefined ? {} : { position: values.position }),
        ...(imageKey === null ? {} : { imageKey }),
        active: values.active === 'active',
      },
      {
        // Muvaffaqiyatdan keyin oyna yopiladi va forma tozalanadi: aks holda operator uni
        // ikkinchi marta yuborib, bitta bandni ikki marta yaratib qo'yishi mumkin edi.
        onSuccess: () => {
          setCreateOpen(false);
          resetForm();
        },
        onError: form.applyServerError,
      },
    );
  });

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        title={strings.rewards.title}
        description={strings.rewards.subtitle}
        actions={
          <FormDialog
            open={createOpen}
            onOpenChange={(open) => {
              setCreateOpen(open);
              // Yopilganda forma tozalanadi — yarim to'ldirilgan forma keyingi ochilishda
              // "kimdir allaqachon boshlagan" degan taassurot qoldirardi.
              if (!open) resetForm();
            }}
            trigger={
              <Button>
                <PlusIcon aria-hidden="true" />
                {strings.rewards.createHeading}
              </Button>
            }
            title={strings.rewards.createHeading}
            submitLabel={createReward.isPending ? strings.rewards.creating : strings.rewards.create}
            submitting={createReward.isPending}
            onSubmit={submitCreate}
            error={form.serverError ? <ErrorNotice>{form.serverError}</ErrorNotice> : null}
          >
            <ImageUploadField
              key={formResetCount}
              kind="reward"
              label={strings.rewards.image}
              onUploaded={setImageKey}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={strings.rewards.name} error={form.formState.errors.name?.message}>
                <Input {...form.register('name')} />
              </Field>

              <Field
                label={strings.rewards.pointsCost}
                error={form.formState.errors.pointsCost?.message}
              >
                {/*
                  `min`/`step` ATAYLAB yo'q: brauzerning o'z HTML5 tekshiruvi noto'g'ri qiymatni
                  jimgina yuborishga qo'ymaydi — o'zbekcha xato xabari esa hech qachon
                  ko'rinmasdi. Tekshiruv sxemada, va uning matni ham o'zbekcha.
                */}
                <Input type="number" {...form.register('pointsCost')} />
              </Field>

              <Field
                label={strings.rewards.position}
                hint={strings.rewards.positionHint}
                error={form.formState.errors.position?.message}
              >
                <Input type="number" {...form.register('position')} />
              </Field>

              <Field label={strings.rewards.state}>
                <Select {...form.register('active')}>
                  <option value="active">{strings.rewards.active}</option>
                  <option value="inactive">{strings.rewards.inactive}</option>
                </Select>
              </Field>
            </div>
          </FormDialog>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>{strings.rewards.listHeading}</CardTitle>
          <CardDescription>{strings.rewards.hint}</CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-4">
          {rewards.isError ? <ErrorNotice error={rewards.error} /> : null}
          {updateReward.isError ? <ErrorNotice error={updateReward.error} /> : null}
          {deleteReward.isError ? <ErrorNotice error={deleteReward.error} /> : null}

          {rewards.isSuccess && rows.length === 0 ? (
            <EmptyState title={strings.rewards.empty} icon={GiftIcon} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{strings.rewards.columnImage}</TableHead>
                  <TableHead>{strings.rewards.columnName}</TableHead>
                  <TableHead className="text-right">{strings.rewards.columnPointsCost}</TableHead>
                  <TableHead className="text-right">{strings.rewards.columnPosition}</TableHead>
                  <TableHead>{strings.rewards.columnState}</TableHead>
                  <TableHead>{strings.rewards.columnActions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rewards.isPending ? <TableSkeleton columns={REWARD_COLUMNS} /> : null}
                {rows.map((reward) => (
                  <RewardRow
                    key={reward.id}
                    reward={reward}
                    onSave={(patch) => updateReward.mutate({ id: reward.id, ...patch })}
                    onDelete={() => deleteReward.mutate(reward.id)}
                    saving={updateReward.isPending}
                    deleting={deleteReward.isPending}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

interface RewardRowProps {
  reward: AdminRewardItem;
  onSave: (patch: AdminRewardUpdate) => void;
  onDelete: () => void;
  saving: boolean;
  deleting: boolean;
}

function RewardRow({ reward, onSave, onDelete, saving, deleting }: RewardRowProps) {
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [imageKey, setImageKey] = useState(reward.imageKey);

  // Qatordagi tahrirlash ham yaratish formasi bilan BIR XIL sxemani ishlatadi: ikkalasi bir
  // xil bandni tavsiflaydi, va ikkita tekshiruv yo'li vaqt o'tishi bilan ajralib ketardi
  // (aynan shu fayl buni allaqachon boshdan kechirgan — ikkita nusxa `Number.isInteger`).
  const form = useAdminForm({ schema: rewardFormSchema, defaultValues: rowValues(reward) });

  const startEdit = () => {
    form.reset(rowValues(reward));
    setImageKey(reward.imageKey);
    setEditing(true);
  };

  const save = form.handleSubmit((values) => {
    onSave({
      name: values.name,
      pointsCost: values.pointsCost,
      position: values.position ?? reward.position,
      ...(imageKey === null ? {} : { imageKey }),
      active: values.active === 'active',
    });
    setEditing(false);
  });

  if (editing) {
    return (
      <TableRow>
        <TableCell colSpan={REWARD_COLUMNS}>
          <div className="flex flex-col gap-4 py-2">
            <ImageUploadField
              kind="reward"
              label={`${strings.rewards.image} — ${reward.name}`}
              imageUrl={reward.imageUrl}
              onUploaded={setImageKey}
              inputAriaLabel={`${strings.rewards.image} — ${reward.name}`}
            />

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <Field label={strings.rewards.name} error={form.formState.errors.name?.message}>
                <Input
                  aria-label={`${strings.rewards.name} — ${reward.name}`}
                  {...form.register('name')}
                />
              </Field>

              <Field
                label={strings.rewards.pointsCost}
                error={form.formState.errors.pointsCost?.message}
              >
                <Input
                  type="number"
                  aria-label={`${strings.rewards.pointsCost} — ${reward.name}`}
                  {...form.register('pointsCost')}
                />
              </Field>

              <Field
                label={strings.rewards.position}
                error={form.formState.errors.position?.message}
              >
                <Input
                  type="number"
                  aria-label={`${strings.rewards.position} — ${reward.name}`}
                  {...form.register('position')}
                />
              </Field>

              <Field label={strings.rewards.state}>
                <Select
                  aria-label={`${strings.rewards.state} — ${reward.name}`}
                  {...form.register('active')}
                >
                  <option value="active">{strings.rewards.active}</option>
                  <option value="inactive">{strings.rewards.inactive}</option>
                </Select>
              </Field>
            </div>

            <div className="flex items-center gap-2">
              <Button size="sm" onClick={save} loading={saving}>
                {saving ? strings.rewards.saving : strings.rewards.save}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditing(false)}
                disabled={saving}
              >
                {strings.rewards.cancel}
              </Button>
            </div>
          </div>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell>
        {reward.imageUrl ? (
          <img
            src={reward.imageUrl}
            alt={strings.uploads.thumbnailAlt}
            className="border-input size-10 rounded-md border object-cover"
          />
        ) : (
          <div className="border-input bg-muted text-muted-foreground flex size-10 items-center justify-center rounded-md border text-[9px]">
            {strings.uploads.noImage}
          </div>
        )}
      </TableCell>
      <TableCell className="font-medium">{reward.name}</TableCell>
      <TableCell className="tabular text-right">{formatNumber(reward.pointsCost)}</TableCell>
      <TableCell className="tabular text-right">{reward.position}</TableCell>
      <TableCell>
        <Badge variant={reward.active ? 'success' : 'outline'}>
          {reward.active ? strings.rewards.active : strings.rewards.inactive}
        </Badge>
      </TableCell>
      <TableCell>
        {/*
          O'chirish ikki bosqichda: birinchi bosish faqat tasdiqni ko'rsatadi, DELETE so'rovi
          esa faqat ikkinchi bosishda ketadi. Bitta tugma bilan qatorni chertib yuborish oson —
          bandning o'zi qaytarilmaydi.
        */}
        {confirmingDelete ? (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-xs">{strings.rewards.deleteConfirm}</span>
            <Button size="sm" variant="destructive" onClick={onDelete} loading={deleting}>
              {deleting ? strings.rewards.deleting : strings.rewards.deleteConfirmYes}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfirmingDelete(false)}
              disabled={deleting}
            >
              {strings.rewards.cancel}
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={startEdit}>
              <PencilIcon aria-hidden="true" />
              {strings.rewards.edit}
            </Button>
            <Button size="sm" variant="destructive" onClick={() => setConfirmingDelete(true)}>
              <TrashIcon aria-hidden="true" />
              {strings.rewards.delete}
            </Button>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}
